/**
 * Application installable et notifications push (vague 5, L0).
 * - `registerAppServiceWorker()` : enregistre `/app-sw.js` (réception du push, clic → conversation,
 *   aucune mise en cache). Jamais avec les maquettes MSW (même portée `/`, un seul service worker).
 * - `pushSupport()` : ce que permet ce navigateur. Sur iPhone, le push n'existe que pour l'application
 *   ajoutée à l'écran d'accueil (iOS 16.4 et plus) : `needsInstall` le signale.
 * - `subscribePush(publicKey)` : demande l'autorisation puis renvoie l'abonnement à transmettre à
 *   `push.subscribe` (L5 appelle la route ; `features.pushPublicKey` vient de `auth.me`).
 */
export const APP_SERVICE_WORKER_URL = '/app-sw.js';

export async function registerAppServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(APP_SERVICE_WORKER_URL, { scope: '/' });
  } catch (err) {
    console.warn('Service worker indisponible :', err);
    return null;
  }
}

export interface PushSupport {
  /** Le navigateur sait recevoir des notifications push. */
  supported: boolean;
  /** iPhone ou iPad hors application installée : « Ajouter à l'écran d'accueil » d'abord. */
  needsInstall: boolean;
  permission: NotificationPermission | 'unsupported';
}

export function pushSupport(): PushSupport {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  return {
    supported,
    needsInstall: ios && !standalone,
    permission: 'Notification' in window ? Notification.permission : 'unsupported',
  };
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Corps de `push.subscribe`, ou `null` si l'utilisateur refuse ou si le navigateur ne sait pas. */
export async function subscribePush(
  publicKey: string,
): Promise<{ endpoint: string; keys: { p256dh: string; auth: string } } | null> {
  if (!pushSupport().supported) return null;
  if ((await Notification.requestPermission()) !== 'granted') return null;
  const registration = (await navigator.serviceWorker.getRegistration('/')) ?? (await registerAppServiceWorker());
  if (!registration) return null;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  return json.endpoint && p256dh && auth ? { endpoint: json.endpoint, keys: { p256dh, auth } } : null;
}

/** Désabonne cet appareil (le serveur est prévenu par `push.unsubscribe` avec l'`endpoint` renvoyé). */
export async function unsubscribePush(): Promise<string | null> {
  const registration = await navigator.serviceWorker?.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return null;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  return endpoint;
}
